import axios from 'axios';
import AuthService from './auth/Auth';
import TokenService from './token/Token';
import qs from 'qs';
import { API_EVENTS, eventsBus } from 'libs/EventsBus';

export const GLOBALLY_HANDLED_STATUSES = {
  401: 'isSessionExpired', // refresh failed / not authenticated — handled: logout + redirect
  403: 'isForbidden', // authenticated but not permitted — often a global "access denied" dialog
  409: 'isConflict', // resource changed elsewhere — handled: toast (already built)
  423: 'isLocked', // resource locked (e.g. someone else is editing it)
  429: 'isRateLimited', // too many requests — often a global "slow down" toast with retry-after
  499: 'isRequestCancelled', // client closed request / cancelled — usually suppressed silently, not toasted
  500: 'isServerError', // generic server error — some apps show one global "something went wrong on our end"
  502: 'isBadGateway', // upstream/gateway failure — often grouped into a global "service unavailable" banner
  503: 'isServiceUnavailable', // maintenance mode / overloaded — often a full-page banner, not a toast
  504: 'isGatewayTimeout', // upstream timed out — similar to 503, often grouped together
};

const MAX_REFRESH_RETRIES = 3;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 10000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getBackoffDelay = (attempt) => {
  const exponential = BASE_BACKOFF_MS * Math.pow(2, attempt);
  return Math.random() * Math.min(exponential, MAX_BACKOFF_MS);
};

const refreshWithRetry = async (retries = MAX_REFRESH_RETRIES) => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await AuthService.refreshAuth();
    } catch (err) {
      const status = err?.response?.status;

      // Refresh token permanently invalid — stop immediately, no retries
      if (status === 401 || status === 403) throw err;

      const isLastAttempt = attempt === retries - 1;
      if (isLastAttempt) throw err;

      // Only retry on network errors or 5xx
      if (status && status < 500) throw err;

      const delay = getBackoffDelay(attempt);
      console.warn(`Refresh attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms…`);
      await wait(delay);
    }
  }
};

const api = axios.create({
  timeout: 60 * 1000,
  baseURL: CONFIG.app.api_base,
  paramsSerializer: (params) => qs.stringify(params, { allowDots: true, indices: false, skipNulls: true }),
});

api.interceptors.request.use(
  (config) => {
    if (config.url === '/auth/refresh') {
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken) config.headers.authorization = refreshToken;
    } else {
      const accessToken = TokenService.getAccessToken();
      if (accessToken) config.headers.authorization = accessToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.request.use(
  (config) => {
    config.params = { ...config.params, portalType: CONFIG.app.portalType };
    return config;
  },
  (error) => Promise.reject(error)
);

let refreshAndRetryQueue = [];
let isRefreshing = false;
let refreshFailed = false; // circuit breaker — latched on hard failure, reset on success

const processQueue = (token, error) => {
  refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      config._retry = true;
      config.headers['authorization'] = `Bearer ${token}`;
      resolve(api.request(config));
    }
  });
  refreshAndRetryQueue = [];
};

api.interceptors.response.use(
  (response) => {
    // Any successful response means we have a valid session — reset circuit breaker
    refreshFailed = false;
    return response;
  },
  async (error) => {
    const originalConfig = error.config;
    const { response } = error;

    const flagName = GLOBALLY_HANDLED_STATUSES[response?.status];
    if (flagName) {
      error[flagName] = true;
    }

    if (response?.status === 401 && originalConfig.url !== '/auth/refresh') {
      // Circuit breaker — refresh already failed definitively this session,
      // reject immediately without attempting another refresh
      if (refreshFailed) {
        return Promise.reject(error);
      }

      // Request already retried after a successful refresh — the server's
      // final answer for this specific request, not a session problem
      if (originalConfig._retry) {
        return Promise.reject(error);
      }

      // Refresh already in flight — park this request until it settles
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshAndRetryQueue.push({ config: originalConfig, resolve, reject });
        });
      }

      // Mark before async work so concurrent 401s hit the isRefreshing branch
      originalConfig._retry = true;
      isRefreshing = true;

      try {
        const { token } = await refreshWithRetry();

        originalConfig.headers['authorization'] = `Bearer ${token}`;
        processQueue(token, null);

        return api.request(originalConfig);
      } catch (refreshError) {
        // Latch circuit breaker — all future 401s will skip refresh entirely.
        // This is the only place logout is called.
        refreshFailed = true;
        processQueue(null, refreshError);

        AuthService.logout();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
